<template>
    <!-- Same pattern as AddSignatureModal: fullscreen restyles the wrapper rather
         than moving the canvas, so the strokes stay put. The buffer is fixed, so
         nothing needs redrawing -- the canvas is only shown bigger. -->
    <div ref="field" :class="{ 'signature-fullscreen': fullscreen }">
      <div class="signature-field-inner">
        <label v-if="label" class="mb-1">{{ label }}</label>
        <div class="signature-rotate-hint small text-body-secondary mb-2">{{ $t('modal.rotateDevice') }}</div>
        <div class="d-flex gap-2">
          <!-- width/height are attributes, not CSS: they size the drawing buffer, and
               the scoped .signature-canvas rule sizes the box. The inline style that
               used to duplicate that rule is gone -- inline wins over any class, so it
               would have kept the border black while invalid. -->
          <canvas ref="canvas" :class="['signature-canvas', { 'is-invalid': error }]" width="480" height="150"></canvas>
          <!-- Fullscreen at the pad's top edge, delete at its bottom edge. -->
          <div class="d-flex flex-column justify-content-between">
            <button type="button" class="btn-icon bg-primary-subtle text-primary-emphasis"
              :class="{ invisible: fullscreen }" :title="$t('modal.fullscreen')" @click="enterFullscreen"><i class="fa-solid fa-expand"></i></button>
            <button class="btn-icon bg-danger-subtle text-danger-emphasis" type="button" :title="$t('modal.clear')" @click="clearCanvas"><i class="fa-regular fa-trash-can"></i></button>
          </div>
        </div>
        <input type="hidden" :name="name" :id="canvasId" :value="value" />
        <div v-if="fullscreen" class="d-flex mt-3">
          <button type="button" class="btn btn-sm btn-primary ms-auto" @click="exitFullscreen">{{ $t('modal.done') }}</button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    props: {
      name: {
        type: String,
        required: true
      },
      value: {
        type: String,
        default: ''
      },
      label: {
        type: String,
        default: ''
      },
      // The root is a wrapper div, so this has to go on the canvas itself.
      error: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        canvasId: `canvas_${Math.random().toString(36).substr(2, 9)}`,
        fullscreen: false
      };
    },
    mounted() {
      this.initializeCanvas();
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
    },
    beforeUnmount() {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      if (this.fullscreen) this.exitFullscreen();
    },
    watch: {
      value(newValue) {
        this.loadImage(newValue);
      }
    },
    methods: {
      // The CSS overlay alone already fills the viewport, so the Fullscreen API
      // and orientation lock are extras: they work on Android Chrome, the lock
      // rejects on desktop, and iOS Safari has neither. Failures are ignored.
      async enterFullscreen() {
        this.fullscreen = true;
        await this.$nextTick();
        try {
          if (this.$refs.field.requestFullscreen) await this.$refs.field.requestFullscreen();
        } catch (e) { /* not supported or denied */ }
        try {
          if (screen.orientation && screen.orientation.lock) await screen.orientation.lock('landscape');
        } catch (e) { /* not supported (desktop, iOS) */ }
      },
      async exitFullscreen() {
        if (!this.fullscreen) return;
        this.fullscreen = false;
        try {
          if (screen.orientation && screen.orientation.unlock) screen.orientation.unlock();
        } catch (e) { /* not supported */ }
        if (document.fullscreenElement) {
          await document.exitFullscreen().catch(() => {});
        }
      },
      // Esc on desktop or the back button on Android leaves browser fullscreen
      // without going through Done; close the overlay with it.
      onFullscreenChange() {
        if (!document.fullscreenElement && this.fullscreen) {
          this.exitFullscreen();
        }
      },
      initializeCanvas() {
        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext('2d');
        let isDrawing = false;

        // The pad can be drawn smaller than its 480x150 buffer (phones), so map
        // the pointer from CSS pixels to buffer pixels.
        const point = (event) => {
          const rect = canvas.getBoundingClientRect();
          const source = event.touches ? event.touches[0] : event;
          return [
            (source.clientX - rect.left) * (canvas.width / rect.width),
            (source.clientY - rect.top) * (canvas.height / rect.height)
          ];
        };

        const startDrawing = (event) => {
          isDrawing = true;
          ctx.beginPath();
          ctx.moveTo(...point(event));
          event.preventDefault();
        };

        const draw = (event) => {
          if (isDrawing) {
            ctx.lineTo(...point(event));
            ctx.stroke();
            event.preventDefault();
          }
        };
  
        const stopDrawing = (event) => {
          if (isDrawing) {
            isDrawing = false;
            ctx.closePath();
            this.updateSignature();
            event.preventDefault();
          }
        };
  
        canvas.addEventListener('mousedown', startDrawing);
        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', stopDrawing);
        canvas.addEventListener('mouseleave', stopDrawing);
  
        canvas.addEventListener('touchstart', startDrawing);
        canvas.addEventListener('touchmove', draw);
        canvas.addEventListener('touchend', stopDrawing);
  
        this.loadImage(this.value);
      },
      clearCanvas() {
        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Not updateSignature(): toDataURL on an empty canvas still returns a
        // valid data URL -- a fully transparent PNG -- so the field would read as
        // signed, satisfying required-field validation and prompting the save
        // warning about a signature that is no longer there.
        this.$emit('update:value', '');
      },
      updateSignature() {
        const canvas = this.$refs.canvas;
        const dataURL = canvas.toDataURL('image/png');
        this.$emit('update:value', dataURL);
      },
      loadImage(dataURL) {
        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!dataURL) return;
        const image = new Image();
        image.src = dataURL;
        image.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Scale the image to fit the canvas while preserving aspect ratio, centered
          const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
          const drawWidth = image.width * scale;
          const drawHeight = image.height * scale;
          const dx = (canvas.width - drawWidth) / 2;
          const dy = (canvas.height - drawHeight) / 2;
          ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
        };
      }
    }
  };
  </script>
  
  <style scoped>
  /* A 480px pad plus the button column, shrinking to fit narrower parents
     (the stacked act table on phones). */
  .signature-field-inner {
    width: calc(480px + 3rem);
    max-width: 100%;
  }

  .signature-canvas {
    /* The aspect ratio keeps the 480x150 buffer undistorted at any width. */
    display: block;
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    height: auto;
    aspect-ratio: 480 / 150;
    border: 1px solid #000;
    /* Strokes are drawn in black and submitted as-is, so the canvas has to stay
       light in both colour modes or the signature disappears. */
    background-color: var(--bs-white);
  }

  .signature-canvas.is-invalid {
    border-color: red;
  }

  /* Above the fixed action bar (1030) and Bootstrap modals (1055). */
  .signature-fullscreen {
    position: fixed;
    inset: 0;
    z-index: 1090;
    background-color: var(--bs-body-bg);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  /* Width follows the available height so the pad keeps its 480:150 ratio. */
  .signature-fullscreen .signature-field-inner {
    max-width: none;
    width: min(100%, calc((100vh - 8rem) * 3.2));
    width: min(100%, calc((100dvh - 8rem) * 3.2));
  }

  .signature-rotate-hint {
    display: none;
  }

  @media (orientation: portrait) {
    .signature-fullscreen .signature-rotate-hint {
      display: block;
    }
  }

  /* Dark mode shows the pad inverted — black backdrop, white strokes. This is a
     CSS filter, so it changes rendering only and never the canvas bitmap: the
     hidden input still carries the same black-on-white toDataURL() the API and
     the server-rendered PDF expect. Here the canvas *is* the bordered element,
     so the filter catches its border too; a mid grey is near enough its own
     inverse to stay neutral, and the invalid state is pre-inverted so it still
     reads as red. */
  [data-bs-theme='dark'] .signature-canvas {
    filter: invert(1);
    border-color: #8A8A8A;
  }

  [data-bs-theme='dark'] .signature-canvas.is-invalid {
    border-color: cyan; /* inverts to red */
  }
  </style>
  